import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  full_name: string;
  userType: 'land_owner' | 'business_owner' | 'investor';
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const TOKEN_CHECK_INTERVAL = 60 * 1000; // Check every minute

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  const isAuthenticated = !!user;

  // Check if token is expired
  const isTokenExpired = useCallback((token: string): boolean => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return true;
    }
  }, []);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
  }, []);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivity > INACTIVITY_TIMEOUT) {
      console.log('Auto-logout due to inactivity');
      // Dispatch auto-logout event
      window.dispatchEvent(new CustomEvent('auto-logout', { 
        detail: { reason: 'inactivity' } 
      }));
      logout();
    }
  }, [lastActivity]);

  // Check token validity
  const checkTokenValidity = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token && isTokenExpired(token)) {
      console.log('Auto-logout due to token expiration');
      // Dispatch auto-logout event
      window.dispatchEvent(new CustomEvent('auto-logout', { 
        detail: { reason: 'expiration' } 
      }));
      logout();
    }
  }, [isTokenExpired]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      updateActivity();
    };

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Check for inactivity every minute
    const inactivityInterval = setInterval(checkInactivity, 60000);
    
    // Check token validity every minute
    const tokenInterval = setInterval(checkTokenValidity, TOKEN_CHECK_INTERVAL);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearInterval(inactivityInterval);
      clearInterval(tokenInterval);
    };
  }, [isAuthenticated, lastActivity, updateActivity, checkInactivity, checkTokenValidity]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      if (!token || !userId) {
        setLoading(false);
        return;
      }

      // Check if token is expired
      if (isTokenExpired(token)) {
        console.log('Token expired on app start');
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
        setLoading(false);
        return;
      }

      try {
        // Verify token with server
        const response = await axios.get('/api/auth/me', {
          headers: { Authorization: `Bearer ${token}` }
        });

        const userData = response.data;
        console.log('AuthContext: User data from /me endpoint:', userData);
        
        // Handle both user_type and userType fields for compatibility
        const userType = userData.user_type || userData.userType;
        const mappedUserType: 'land_owner' | 'business_owner' | 'investor' = 
          userType === 'land_owner' ? 'land_owner' : 
          userType === 'business_owner' ? 'business_owner' : 'investor';
        
        const mappedUser: User = {
          id: userData.id,
          email: userData.email,
          full_name: userData.full_name,
          userType: mappedUserType
        };
        
        console.log('AuthContext: Mapped user object:', mappedUser);
        setUser(mappedUser);
      } catch (error) {
        console.log('Token verification failed');
        localStorage.removeItem('token');
        localStorage.removeItem('userId');
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [isTokenExpired]);

  const login = async (token: string, user: User) => {
    try {
      localStorage.setItem('token', token);
      localStorage.setItem('userId', user.id.toString());
      setUser(user);
      updateActivity();
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Greška pri prijavljivanju');
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await axios.post('/api/auth/register', userData);
      if (response.status === 201) {
        // Registration successful, email sent. Do not auto-login.
        return;
      } else {
        throw new Error(response.data?.message || 'Registracija nije uspela');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registracija nije uspela');
    }
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        // Call logout endpoint to invalidate token on server
        await axios.post('/api/auth/logout', {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      setUser(null);
    }
  };

  const value = {
    user,
    isAuthenticated,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 